import { useRef } from 'react'

import styles from './FlowStageTabs.module.css'

interface Stage {
  key: string
  label: string
  count: number | null
}

interface FlowStageTabsProps {
  stages: Stage[]
  activeKey: string
  onChange: (key: string) => void
}

function FlowStageTabs({ stages, activeKey, onChange }: FlowStageTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      const newIndex = currentIndex > 0 ? currentIndex - 1 : stages.length - 1
      const buttons = containerRef.current?.querySelectorAll('[role="tab"]')
      if (buttons && buttons[newIndex]) {
        (buttons[newIndex] as HTMLElement).focus()
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      const newIndex = currentIndex < stages.length - 1 ? currentIndex + 1 : 0
      const buttons = containerRef.current?.querySelectorAll('[role="tab"]')
      if (buttons && buttons[newIndex]) {
        (buttons[newIndex] as HTMLElement).focus()
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onChange(stages[currentIndex].key)
    }
  }

  const formatCount = (count: number | null): string => {
    return count !== null && count > 0 ? count.toString() : '--'
  }

  return (
    <div
      ref={containerRef}
      role="tablist"
      className={styles.container}
      aria-label="Job application stages"
    >
      {stages.map((stage, index) => {
        const isActive = activeKey === stage.key
        const hasData = stage.count !== null && stage.count > 0
        const isLast = index === stages.length - 1

        return (
          <button
            key={stage.key}
            role="tab"
            aria-selected={isActive}
            aria-controls={`stage-${stage.key}`}
            tabIndex={isActive ? 0 : -1}
            className={`${styles.stage} ${isLast ? styles.last : ''} ${
              hasData ? styles.hasData : styles.empty
            } ${isActive ? styles.active : ''}`}
            onClick={() => onChange(stage.key)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            <span className={styles.inner}>
              <span className={styles.count}>{formatCount(stage.count)}</span>
              <span className={styles.label}>{stage.label.toUpperCase()}</span>
            </span>
            {!isLast && (
              <span className={styles.innerTriangle} />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default FlowStageTabs

/*
Usage example:

<FlowStageTabs
  stages={[
    { key: 'bookmarked', label: 'Bookmarked', count: 38 },
    { key: 'applying', label: 'Applying', count: null },
    { key: 'applied', label: 'Applied', count: 20 },
    { key: 'interviewing', label: 'Interviewing', count: 3 },
    { key: 'negotiating', label: 'Negotiating', count: null },
    { key: 'accepted', label: 'Accepted', count: null },
  ]}
  activeKey="bookmarked"
  onChange={(key) => {}}
/>
*/

